import React from 'react';
import { Field, Checkbox, NumericInput, TextOutput, RadioGroup } from '../../../Core';
import { ListFieldSection, FieldSet } from '../../../Layout';
import { withInjectedProps } from '../../../Core/InjectedPropsContext';
import { useSelector } from 'react-redux';
import { WorksheetEquipmentDropdown } from '../../Core';
import deepEquals from '../../../../Lib/Object/deepEquals';
import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { pipe } from 'Lib/Hooks/Selectors';
import { WorksheetBalanceFieldSet } from '../../Core/WorksheetEquipment';

let CompactionEquipmentSection: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }) => {
  const mouldEquipment = useSelector(createWorksheetSelector!(`Equipment.Mould`), deepEquals);
  const mouldMass = useSelector(createWorksheetSelector!(`Equipment.MouldMass`), deepEquals);
  const mouldVolume = useSelector(createWorksheetSelector!(`Equipment.MouldVolume`), deepEquals);
  const mouldCalibrationMismatch = useSelector(createWorksheetSelector!(`Equipment.MouldCalibrationMismatch`));

  const hammerEquipment = useSelector(createWorksheetSelector!(`Equipment.Hammer`), deepEquals);
  const hammerOperation = useSelector(createWorksheetSelector!(`Equipment.HammerOperation`));
  const hammerFaceType = useSelector(createWorksheetSelector!(`Equipment.HammerFaceType`));
  const hammerFaceNominalDiameter = useSelector(createWorksheetSelector!(`Equipment.HammerFaceDiameter`), deepEquals);
  const hammerFaceNominalRadius = useSelector(createWorksheetSelector!(`Equipment.HammerFaceRadius`), deepEquals);
  const hammerFaceNominalUnits = useSelector(createWorksheetSelector!(`Equipment.HammerFaceNominalUnits`));
  const hammerImportMismatch = useSelector(createWorksheetSelector!(`Equipment.HammerImportMismatch`));

  /*
   * Determines whether a test portion has any density mould measurements that would be
   * overwritten when checking the 'use for all test portions' checkbox.
   */
  const hasNonMatchingMould = (portion: any) => {
    var portionMouldId = portion.Mould && portion.Mould.Id;
    var sampleMouldId = mouldEquipment && mouldEquipment.Id;
    var portionMouldMass = portion.MouldMass && portion.MouldMass.value;
    var sampleMouldMass = mouldMass && mouldMass.value;
    var portionMouldVolume = portion.MouldVolume && portion.MouldVolume.value;
    var sampleMouldVolume = mouldVolume && mouldVolume.value;
    var nonMatchingMouldId = portionMouldId && portionMouldId !== sampleMouldId;
    var nonMatchingMouldMass = portionMouldMass && portionMouldMass !== sampleMouldMass;
    var nonMatchingMouldVolume = portionMouldVolume && portionMouldVolume !== sampleMouldVolume;
    return nonMatchingMouldId || nonMatchingMouldMass || nonMatchingMouldVolume;
  };

  const testPortionHasNonMatchingMould = useSelector(
    pipe(createWorksheetSelector!(`TestPortions`), (p) => p.some(hasNonMatchingMould))
  );

  return (
    <ListFieldSection heading="Equipment">
      <WorksheetBalanceFieldSet label="Balance" name="Equipment.CompactionBalance" />
      <FieldSet>
        <Field
          input={<WorksheetEquipmentDropdown />}
          label="Mold"
          equipmentType="density-mould"
          name={`Equipment.Mould`}
          importedCalibration={{
            Mass: mouldMass,
            Volume: mouldVolume,
            Mismatch: mouldCalibrationMismatch,
          }}
          importModel={{
            Id: mouldEquipment && mouldEquipment.Id,
          }}
        />
        <Field
          input={<Checkbox />}
          label="Use for all test portions"
          name={`Equipment.UseMouldForAllTestPortions`}
          confirm={({ value }) => value === true && testPortionHasNonMatchingMould}
          confirmText="The selected mold will be used for all test portions, overwriting any existing data."
          confirmButtonText="Use mold"
        />
        <Field
          input={<NumericInput />}
          label="Mass"
          name={`Equipment.MouldMass`}
          disabled={mouldEquipment && mouldEquipment.Mass}
        />
        <Field
          input={<NumericInput />}
          label="Volume"
          name={`Equipment.MouldVolume`}
          disabled={mouldEquipment && mouldEquipment.Volume}
        />
      </FieldSet>
      <FieldSet>
        <Field
          input={<WorksheetEquipmentDropdown />}
          label="Hammer"
          equipmentType="hammer"
          name="Equipment.Hammer"
          importedAdditionalData={{
            Operation: hammerOperation,
            FaceType: hammerFaceType,
            FaceDiameter: hammerFaceNominalDiameter,
            FaceRadius: hammerFaceNominalRadius,
            NominalUnits: hammerFaceNominalUnits,
            Mismatch: hammerImportMismatch,
          }}
          importModel={{
            Id: hammerEquipment && hammerEquipment.Id,
          }}
        />
        <Field
          input={<RadioGroup />}
          label="Operation"
          name={`Equipment.HammerOperation`}
          visible={!(!!hammerEquipment && !!hammerEquipment.Operation)}
          options={['Automatic', 'Manual'].map((e) => ({ label: e, value: e }))}
        />
        <Field
          input={<TextOutput />}
          label="Operation"
          name={`Equipment.HammerOperation`}
          visible={!!hammerEquipment && !!hammerEquipment.Operation}
        />
        <Field
          input={<RadioGroup />}
          label="Face Type"
          name="Equipment.HammerFaceType"
          visible={!(!!hammerEquipment && !!hammerEquipment.FaceType)}
          options={[
            { label: 'Circular', value: 'circular' },
            { label: 'Sector (Pie)', value: 'sector' },
          ]}
        />

        <Field
          input={<TextOutput />}
          label="Face Type"
          visible={!!hammerEquipment && !!hammerEquipment.FaceType}
          name={`Equipment.HammerFaceType`}
          value={
            [
              { label: 'Circular', value: 'circular' },
              { label: 'Sector (Pie)', value: 'sector' },
            ].find((o) => o.value === hammerFaceType)?.label
          }
        />
        <Field
          input={<NumericInput />}
          label="Face Nominal Diameter"
          name="Equipment.HammerFaceDiameter"
          visible={!(!!hammerEquipment && !!hammerEquipment.FaceDiameter) && hammerFaceType === 'circular'}
        />
        <Field
          input={<TextOutput />}
          label="Face Nominal Diameter"
          name="Equipment.HammerFaceDiameter"
          visible={!!hammerEquipment && !!hammerEquipment.FaceDiameter && hammerFaceType === 'circular'}
        />

        <Field
          input={<NumericInput />}
          label="Face Nominal Radius"
          name="Equipment.HammerFaceRadius"
          visible={!(!!hammerEquipment && !!hammerEquipment.FaceRadius) && hammerFaceType === 'sector'}
        />
        <Field
          input={<TextOutput />}
          label="Face Nominal Radius"
          name="Equipment.HammerFaceRadius"
          visible={!!hammerEquipment && !!hammerEquipment.FaceRadius && hammerFaceType === 'sector'}
        />
      </FieldSet>
    </ListFieldSection>
  );
};

CompactionEquipmentSection = withInjectedProps(CompactionEquipmentSection);

export { CompactionEquipmentSection };
export default CompactionEquipmentSection;
